import {
  Box,
  Center,
  Circle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  IconButton,
  Popover,
  Flex,
  Input,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FilterLines } from '../../../utils/constants/icons'
import { Check, ChevronDown, X } from 'lucide-react'
import { useState } from 'react'
import { useHideTabBar } from '../../../hooks/useHideTabBar'

// Styled Menu Dropdown Component for use inside FilterPopover
export const FilterMenuDropdown = ({
  value,
  onChange,
  options,
  placeholder = 'Select option',
  searchPlaceholder = 'Search...',
}: {
  value: string
  onChange: (value: string) => void
  options: Array<{ label: string; value: string } | null>
  placeholder?: string
  searchPlaceholder?: string
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const selectedOption = options.find((opt) => opt && opt.value === value)
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false })

  const filteredOptions = options.filter(
    (opt) => !opt || opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <Menu>
      <MenuButton
        as={Button}
        w="100%"
        h={{ base: '48px', md: '40px' } as any}
        bg="white"
        borderRadius="7px"
        fontSize={{ base: '16px', md: '13px' } as any}
        fontWeight="500"
        color={selectedOption ? 'neutral.700' : 'neutral.400'}
        px="12px"
        borderWidth="0"
        boxShadow="0 1px 3px 0 rgba(0,0,0,0.12), 0 1px 2px -1px rgba(0,0,0,0.08)"
        transition="all 0.15s"
        _hover={{
          boxShadow: '0 3px 8px 0 rgba(0,0,0,0.14), 0 1px 3px -1px rgba(0,0,0,0.1)',
        }}
        _active={{
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.1)',
          transform: 'scale(0.98)',
        }}
      >
        <Flex align="center" justify="space-between" w="100%">
          <Text fontSize={{ base: '15px', md: '13px' } as any} noOfLines={1}>
            {selectedOption ? selectedOption.label : placeholder}
          </Text>
          <ChevronDown size={isMobile ? 16 : 14} />
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList
          maxH="350px"
          minW={{ base: '280px', md: '250px' } as any}
          maxW={{ base: 'calc(100vw - 32px)', md: '350px' } as any}
          zIndex={9999}
          p="4px"
          borderRadius="8px"
          boxShadow="0 4px 12px 0 rgba(0,0,0,0.12), 0 2px 6px -1px rgba(0,0,0,0.08)"
        >
          <Box p="8px 8px 12px 8px">
            <Input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              h={{ base: '44px', md: '36px' } as any}
              fontSize={{ base: '16px', md: '13px' } as any}
              borderRadius="7px"
              bg="white"
              borderWidth="0"
              boxShadow="0 1px 3px 0 rgba(0,0,0,0.12), 0 1px 2px -1px rgba(0,0,0,0.08)"
              _focus={{
                boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1), 0 1px 3px 0 rgba(0,0,0,0.12)',
                borderColor: 'primary.500',
              }}
            />
          </Box>
          <Box maxH="250px" overflowY="auto" px="4px">
            {filteredOptions.filter(Boolean).length === 0 ? (
              <Box p="12px" textAlign="center">
                <Text fontSize="13px" color="neutral.400">
                  No results found
                </Text>
              </Box>
            ) : (
              filteredOptions.map((option, index) => {
                if (!option) {
                  return <Box key={`divider-${index}`} h="1px" bg="neutral.100" my="6px" />
                }
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => onChange(value === option.value ? '' : option.value)}
                    fontSize={{ base: '15px', md: '13px' } as any}
                    h={{ base: '44px', md: '36px' } as any}
                    borderRadius="6px"
                    bg={value === option.value ? 'primary.50' : 'transparent'}
                    _hover={{ bg: value === option.value ? 'primary.100' : 'neutral.50' }}
                    mb={index < filteredOptions.length - 1 ? '2px' : '0'}
                  >
                    <Flex align="center" justify="space-between" w="100%">
                      <Text>{option.label}</Text>
                      {value === option.value && (
                        <Icon as={Check} w="16px" h="16px" color="primary.500" strokeWidth={3} />
                      )}
                    </Flex>
                  </MenuItem>
                )
              })
            )}
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  )
}

const FilterPopover = ({
  subheader,
  children,
  isActive = false,
  isOpen: controlledIsOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
}: {
  subheader: string
  children: React.ReactNode | ((props: { onClose: () => void }) => React.ReactNode)
  isActive?: boolean
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
}) => {
  const controlledProps =
    controlledIsOpen !== undefined
      ? { isOpen: controlledIsOpen, onOpen: controlledOnOpen, onClose: controlledOnClose }
      : {}
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false })
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)

  const isOpenResolved = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledOpen

  // Hide the bottom tab bar while the mobile filter drawer is open so the
  // tab bar doesn't overlap the filter form fields / Apply CTA.
  useHideTabBar(!!isMobile && !!isOpenResolved, 'filter-popover')

  const handleOpen = () => {
    if (controlledOnOpen) controlledOnOpen()
    else setUncontrolledOpen(true)
  }
  const handleClose = () => {
    if (controlledOnClose) controlledOnClose()
    else setUncontrolledOpen(false)
  }

  const triggerButton = (
    <Box position="relative">
      <IconButton
        aria-label="filter"
        variant="unstyled"
        maxW="32px"
        minW="32px"
        h="32px"
        bg="white"
        color="secondary.500"
        borderRadius="7px"
        borderWidth="0"
        boxShadow="0 1px 3px 0 rgba(0,0,0,0.12), 0 1px 2px -1px rgba(0,0,0,0.08)"
        transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
        _hover={{
          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.15), 0 2px 4px -1px rgba(0,0,0,0.1)',
          transform: 'scale(1.08)',
          bg: 'neutral.50',
        }}
        _active={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.1)', transform: 'scale(0.95)' }}
        icon={
          <Icon viewBox="0 0 24 24" w="16px" maxW="16px" h="16px" fill="none">
            <FilterLines />
          </Icon>
        }
      />
      {isActive && (
        <Circle
          size="10px"
          bg="red.500"
          position="absolute"
          top="-2px"
          right="-2px"
          borderWidth="1px"
          borderColor="white"
        />
      )}
    </Box>
  )

  const contentBodyStyles = {
    '& > p': {
      fontSize: '12px',
      fontWeight: '600',
      color: 'neutral.500',
      letterSpacing: '0.5px',
      marginBottom: '8px',
      marginTop: '16px',
      _first: {
        marginTop: '0',
      },
    },
    '& select': {
      height: isMobile ? '48px' : '40px',
      fontSize: isMobile ? '16px' : '13px',
      fontWeight: '500',
      color: 'neutral.800',
      bg: 'neutral.10',
      borderRadius: '7px',
      borderWidth: '1px',
      borderColor: '#E8EDF3',
      transition: 'all 0.15s',
      _hover: {
        boxShadow: '0 3px 8px 0 rgba(0,0,0,0.14), 0 1px 3px -1px rgba(0,0,0,0.1)',
      },
      _focus: {
        bg: 'white',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1), 0 1px 3px 0 rgba(0,0,0,0.12)',
        borderColor: 'transparent',
      },
    },
    '& input[type="text"], & input[type="search"]': {
      height: isMobile ? '48px' : '40px',
      fontSize: isMobile ? '16px' : '13px',
      fontWeight: '500',
      color: 'neutral.800',
      bg: 'neutral.10',
      borderRadius: '7px',
      borderWidth: '1px',
      borderColor: '#E8EDF3',
      transition: 'all 0.15s',
      _hover: {
        boxShadow: '0 3px 8px 0 rgba(0,0,0,0.14), 0 1px 3px -1px rgba(0,0,0,0.1)',
      },
      _focus: {
        bg: 'white',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1), 0 1px 3px 0 rgba(0,0,0,0.12)',
        borderColor: 'transparent',
      },
    },
  }

  if (isMobile) {
    return (
      <>
        <Box onClick={handleOpen}>{triggerButton}</Box>
        <Drawer
          isOpen={!!isOpenResolved}
          placement="bottom"
          onClose={handleClose}
          autoFocus={false}
        >
          <DrawerOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
          <DrawerContent
            borderTopRadius="20px"
            maxH="90dvh"
            pb="env(safe-area-inset-bottom, 0px)"
          >
            <Center pt="10px" pb="4px">
              <Box w="40px" h="4px" borderRadius="full" bg="neutral.200" />
            </Center>
            <DrawerHeader p="14px 20px 14px 20px" borderBottomWidth="1px" borderColor="neutral.100">
              <Flex alignItems="center" gap="10px">
                <Center background="linear-gradient(135deg, #818CF8, #6366F1)" borderRadius="full" w="32px" h="32px" flexShrink={0}>
                  <Icon w="16px" h="16px" viewBox="0 0 24 24" color="white">
                    <FilterLines />
                  </Icon>
                </Center>
                <Box flex="1" minW={0}>
                  <Text fontSize="16px" fontWeight="700" color="neutral.900" lineHeight="1.3">
                    Filter
                  </Text>
                  <Text fontSize="12px" color="neutral.400" lineHeight="1.3">
                    {subheader}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Close"
                  icon={<Icon as={X} w="20px" h="20px" />}
                  variant="ghost"
                  size="sm"
                  color="neutral.500"
                  onClick={handleClose}
                />
              </Flex>
            </DrawerHeader>
            <DrawerBody
              p="16px 20px 24px 20px"
              onClick={(e) => e.stopPropagation()}
              sx={contentBodyStyles as any}
            >
              {typeof children === 'function' ? children({ onClose: handleClose }) : children}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover closeOnBlur={false} closeOnEsc={true} isLazy {...controlledProps}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>{triggerButton}</PopoverTrigger>
          <Portal>
            <PopoverContent
              borderRadius="14px"
              boxShadow="0px 8px 30px -6px rgba(0, 0, 0, 0.18)"
              border="1px solid"
              borderColor="neutral.100"
              w="auto"
              minW="fit-content"
              maxW="none"
              onClick={(e) => e.stopPropagation()}
              _focus={{ outline: 'none' }}
              zIndex={1400}
            >
              <PopoverCloseButton top="14px" right="12px" color="neutral.400" />
              <PopoverBody p="0">
                {/* Header */}
                <Flex px="20px" pt="18px" pb="14px" alignItems="center" gap="10px">
                  <Center background="linear-gradient(135deg, #818CF8, #6366F1)" borderRadius="full" w="32px" h="32px">
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" color="white">
                      <FilterLines />
                    </Icon>
                  </Center>
                  <Box>
                    <Text fontSize="14px" fontWeight="700" color="neutral.900" lineHeight="1.3">
                      Filter
                    </Text>
                    <Text fontSize="12px" color="neutral.400" lineHeight="1.3">
                      {subheader}
                    </Text>
                  </Box>
                </Flex>

                {/* Content */}
                <Box
                  px="20px"
                  pb="20px"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    // Label styling — matches AdvancedSiteFiltersModal
                    '& > p': {
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'neutral.500',
                      letterSpacing: '0.5px',
                      marginBottom: '8px',
                      marginTop: '16px',
                      _first: {
                        marginTop: '0',
                      },
                    },
                    // Select dropdown styling
                    '& select': {
                      height: '40px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: 'neutral.800',
                      bg: 'neutral.10',
                      borderRadius: '7px',
                      borderWidth: '1px',
                      borderColor: '#E8EDF3',
                      transition: 'all 0.15s',
                      _hover: {
                        boxShadow: '0 3px 8px 0 rgba(0,0,0,0.14), 0 1px 3px -1px rgba(0,0,0,0.1)',
                      },
                      _focus: {
                        bg: 'white',
                        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1), 0 1px 3px 0 rgba(0,0,0,0.12)',
                        borderColor: 'transparent',
                      },
                    },
                    // Input styling (if any direct inputs)
                    '& input[type="text"], & input[type="search"]': {
                      height: '40px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: 'neutral.800',
                      bg: 'neutral.10',
                      borderRadius: '7px',
                      borderWidth: '1px',
                      borderColor: '#E8EDF3',
                      transition: 'all 0.15s',
                      _hover: {
                        boxShadow: '0 3px 8px 0 rgba(0,0,0,0.14), 0 1px 3px -1px rgba(0,0,0,0.1)',
                      },
                      _focus: {
                        bg: 'white',
                        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1), 0 1px 3px 0 rgba(0,0,0,0.12)',
                        borderColor: 'transparent',
                      },
                    },
                  }}
                >
                  {typeof children === 'function' ? children({ onClose }) : children}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  )
}

export default FilterPopover
